/** biome-ignore-all lint/complexity/noStaticOnlyClass: ignoring */
import { isDefined, isNotDefined } from '#misc';

import type { StorageEngineOptions } from './types/storage-engine.options';

import { StorageEngine } from './storage-engine';

/**
 * Factory class for managing singleton instances of {@link StorageEngine}.
 *
 * Provides methods to create, retrieve, and destroy storage engine instances
 * based on a namespace and channel name. Instances are cached internally
 * to ensure only one instance exists per unique key.
 *
 * @remarks
 * - The key for each instance is constructed as `${namespace}::${channelName}`.
 * - By default, the channel name is `'browser-storage'`.
 * - Use {@link StorageEngineFactory.getInstance} to retrieve or create an instance.
 * - Use {@link StorageEngineFactory.destroy} to destroy and remove an instance.
 */
export class StorageEngineFactory {
  private static instances = new Map<string, StorageEngine>();

  /**
   * Destroys the storage engine instance associated with the given namespace and channel name.
   * @param namespace - The namespace of the storage engine instance to destroy.
   * @param channelName - The channel name of the storage engine instance to destroy (default: 'browser-storage').
   */
  static destroy(namespace: string, channelName = 'browser-storage') {
    const key = `${namespace}::${channelName}`;
    const instance = StorageEngineFactory.instances.get(key);

    if (instance) {
      instance.destroy();
      StorageEngineFactory.instances.delete(key);
    }
  }

  /**
   * Destroys all storage engine instances managed by the factory.
   *
   * Iterates through all registered instances, calls their `destroy` method,
   * and removes them from the internal collection.
   *
   * @remarks
   * This method is useful for cleanup operations, ensuring that all resources
   * held by storage engines are properly released.
   */
  static destroyAll() {
    for (const [key, instance] of StorageEngineFactory.instances) {
      instance.destroy();
      StorageEngineFactory.instances.delete(key);
    }
  }

  /**
   * Retrieves the storage engine instance associated with the given namespace and channel name.
   * If the instance does not exist, a new one is created.
   * @param storage - The storage object to be used by the storage engine.
   * @param options - Optional configuration options for the storage engine.
   * @returns The storage engine instance.
   */
  static getInstance(storage: Storage, options?: StorageEngineOptions): StorageEngine {
    const namespace = options?.namespace ?? '';
    const channel = options?.channelName ?? 'browser-storage';
    const createInstance = (instanceKey: string) => {
      const engine = new StorageEngine(storage, options);

      StorageEngineFactory.instances.set(instanceKey, engine);
    };

    const key = `${namespace}::${channel}`;

    if (!StorageEngineFactory.instances.has(key)) {
      createInstance(key);
    }

    let instance = StorageEngineFactory.instances.get(key);

    if (isDefined(instance) && instance.isDestroyed) {
      createInstance(key);

      instance = StorageEngineFactory.instances.get(key);
    }

    if (isNotDefined(instance)) {
      throw new Error(`Failed to create or retrieve storage engine instance for key: ${key}`);
    }

    return instance;
  }
}
